/**
 * Gas Account API services
 * Uses gas account namespace (VITE_API_GAS_ACCOUNT_NAMESPACE), not main namespace
 */

import authorizedRequestGasAccount, {
  authorizedRequestGasAccountV2,
} from "@/lib/axios/authorizedRequestGasAccount"

// GET gas-account-balance response
export interface GasAccountBalancePending {
  deposit: string
  withdraw: string
  usage: string
  transferSponsor: string
  total: string
}

export interface GasAccountBalanceComputed {
  effectiveBalance: string
  projectedBalance: string
}

export interface GasAccountBalanceData {
  availableBalance: string
  totalDeposited: string
  totalWithdrawn: string
  totalUsed: string
  totalTransferSponsor: string
  pending: GasAccountBalancePending
  computed: GasAccountBalanceComputed
  lastUpdatedAt: string
}

export interface GasAccountBalanceResponse {
  status: boolean
  data: GasAccountBalanceData
  code?: number
}

export interface StableTokenImage {
  thumb: string
  small: string
  large: string
}

export type DepositTokenType = "USD_STABLE_TOKEN" | "JPYC"

export interface StableToken {
  id: string
  chainId: number
  address: string
  name: string
  symbol: string
  decimals: number
  image: StableTokenImage | null
  isActive: boolean
  tokenType?: DepositTokenType
  metadata?: {
    EIP3009?: {
      name?: string
      version?: string
    }
    [key: string]: unknown
  } | null
  createdAt: string
  updatedAt: string
}

export type DepositTokenV2 = StableToken

export interface StableTokensResponse {
  status: boolean
  data: StableToken[]
  code?: number
}

export type DepositTokensV2Response = StableTokensResponse

export type DepositVaultsMap = Record<string, string>

export interface DepositVaultsResponse {
  status: boolean
  data: DepositVaultsMap
  code?: number
}

export interface GasAccountTransactionItem {
  id: string
  type?: string
  transactionType?: string
  historyType?: AccountingHistoryType
  status?: string
  amount?: string
  amountInUsd?: string
  txHash?: string | null
  tokenAddress?: string
  chainId?: number
  direction?: "IN" | "OUT" | string
  toAddress?: string | null
  blockNumber?: number
  confirmations?: number
  logIndex?: number
  notes?: string
  actor?: string
  parentId?: string | null
  sponsorType?: string | null
  sponsorGasAccountId?: string | null
  depositId?: string
  usageId?: string
  transferSponsorId?: string
  createdAt?: string
  updatedAt?: string
  network?: { chainId: number }
  details?: {
    request?: Record<string, unknown>
    tx?: Record<string, unknown>
  }
  [key: string]: unknown
}

export interface GasAccountTransactionListData {
  address: string
  transactions: GasAccountTransactionItem[]
  filters: {
    type: string | null
    status: string | null
  }
}

export interface GasAccountTransactionListMeta {
  current_page: number
  next_page: number | null
  prev_page: number | null
  total_pages: number
  total_count: number
}

export interface GasAccountTransactionListResponse {
  status: boolean
  data: GasAccountTransactionListData
  meta: GasAccountTransactionListMeta
  code?: number
}

export interface GasAccountTransactionDetailData {
  address: string
  transaction: GasAccountTransactionItem
  timestamp?: string
}

export interface GasAccountTransactionDetailResponse {
  status?: boolean
  data?: GasAccountTransactionDetailData
  code?: number
}

export interface UpdateBalancesRequest {
  addresses: string[]
}

export interface UpdateBalancesResponse {
  status?: boolean
  data?: unknown
}

export interface DepositRequest {
  tokenAddress: string
  chainId: number
  txHash: string
  amount: string
  actor: string
}

export interface DepositResponse {
  status?: boolean
  data?: unknown
}

export interface UseBalanceRequest {
  toAddress: string
  chainId: string
  gasLimit: number
  gasPrice: number
}

export interface UseBalanceResponse {
  status?: boolean
  data?: unknown
}

export type AccountingHistoryType =
  | "DEPOSIT"
  | "USAGE"
  | "TRANSFER_SPONSOR"
  | "ADJUSTMENT"

export interface AccountingAddressBalanceV2 {
  address: string
  balances: Array<{
    accountType: string
    balance: string
    expiredAt: string | null
  }>
  effectiveBalance: string
}

export interface AccountingAddressBalanceV2Response {
  status: boolean
  data: AccountingAddressBalanceV2
  code?: number
}

export interface AccountingHistoryV2Response {
  status: boolean
  data: GasAccountTransactionItem[]
  meta: GasAccountTransactionListMeta
  code?: number
}

export enum DepositV2StatusValue {
  Pending = "PENDING",
  Confirmed = "CONFIRMED",
  Failed = "FAILED",
  Cancelled = "CANCELLED",
}

export type DepositV2Status = DepositV2StatusValue | string

export interface DepositV2 {
  id: string
  status: DepositV2Status
  txHash?: string | null
  tokenAddress?: string
  chainId?: number
  amount?: string
  amountInUsd?: string
  actor?: string
  tokenType?: DepositTokenType
  createdAt?: string
  updatedAt?: string
  [key: string]: unknown
}

export interface DepositV2Response {
  status: boolean
  data: DepositV2
  code?: number
}

export interface CreateDepositV2Request {
  tokenAddress: string
  chainId: number
  amount: string
  actor: string
  txHash?: string | null
}

export enum UpdateDepositV2Intent {
  Submit = "SUBMIT",
  SpeedUp = "SPEED_UP",
  Cancel = "CANCEL",
}

export interface UpdateDepositV2Request {
  intent: UpdateDepositV2Intent
  txHash?: string | null
}

export interface JpycDepositQuoteV2Request {
  tokenAddress: string
  chainId: number
  amount: string
  actor: string
}

export interface JpycDepositQuoteV2 {
  quoteId?: string
  grossAmount?: string
  gasFee?: string
  netAmount?: string
  validAfter?: number
  validBefore?: number
  nonce?: string
  expiresAt?: string
  [key: string]: unknown
}

export interface JpycDepositQuoteV2Response {
  status: boolean
  data: JpycDepositQuoteV2
  code?: number
}

export interface CreateJpycDepositV2Request extends JpycDepositQuoteV2Request {
  quoteId?: string
  signature: string
  authorization?: Record<string, unknown>
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function getGasAccountTransactionsV1(params?: {
  page?: number
  per?: number
}): Promise<GasAccountTransactionListResponse> {
  const queryParams = new URLSearchParams()
  if (params?.page != null) queryParams.append("page", String(params.page))
  if (params?.per != null) queryParams.append("per", String(params.per))
  const query = queryParams.toString()
  const url = `gas-account-transaction${query ? `?${query}` : ""}`
  const response =
    await authorizedRequestGasAccount.get<GasAccountTransactionListResponse>(url)
  return response as unknown as GasAccountTransactionListResponse
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function getGasAccountTransactionByIdV1(
  id: string
): Promise<GasAccountTransactionDetailResponse> {
  const response =
    await authorizedRequestGasAccount.get<GasAccountTransactionDetailResponse>(
      `gas-account-transaction/${id}`
    )
  return response as unknown as GasAccountTransactionDetailResponse
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function getStableTokensV1(): Promise<StableTokensResponse> {
  const response =
    await authorizedRequestGasAccount.get<StableTokensResponse>(`stable_tokens`)
  return response as unknown as StableTokensResponse
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function getDepositVaultsV1(): Promise<DepositVaultsResponse> {
  const response =
    await authorizedRequestGasAccount.get<DepositVaultsResponse>(
      `deposit_vaults`
    )
  return response as unknown as DepositVaultsResponse
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function getGasAccountBalanceV1(): Promise<GasAccountBalanceResponse> {
  const response =
    await authorizedRequestGasAccount.get<GasAccountBalanceResponse>(
      `gas-account-balance`
    )
  return response as unknown as GasAccountBalanceResponse
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function updateGasAccountBalancesV1(
  payload: UpdateBalancesRequest
): Promise<UpdateBalancesResponse> {
  const response =
    await authorizedRequestGasAccount.put<UpdateBalancesResponse>(
      `gas-account-balance/balances`,
      payload
    )
  return response as unknown as UpdateBalancesResponse
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function depositGasAccountTransactionV1(
  payload: DepositRequest
): Promise<DepositResponse> {
  const response = await authorizedRequestGasAccount.post<DepositResponse>(
    `gas-account-transaction/deposit`,
    payload
  )
  return response as unknown as DepositResponse
}

/** Legacy v1. Kept temporarily during v2 rollout; remove after migration stabilizes. */
export async function postUseGasAccountBalanceV1(
  payload: UseBalanceRequest
): Promise<UseBalanceResponse> {
  const response = await authorizedRequestGasAccount.post<UseBalanceResponse>(
    `gas-account-transaction/use-balance`,
    payload
  )
  return response as unknown as UseBalanceResponse
}

export async function getDepositTokensV2(params?: {
  chainId?: number
  tokenType?: DepositTokenType
}): Promise<DepositTokensV2Response> {
  const queryParams = new URLSearchParams()
  if (params?.chainId != null) queryParams.append("chainId", String(params.chainId))
  if (params?.tokenType) queryParams.append("tokenType", params.tokenType)
  const query = queryParams.toString()
  const response =
    await authorizedRequestGasAccountV2.get<DepositTokensV2Response>(
      `public/deposit_tokens${query ? `?${query}` : ""}`
    )
  return response as unknown as DepositTokensV2Response
}

export async function getDepositVaultsV2(): Promise<DepositVaultsResponse> {
  const response =
    await authorizedRequestGasAccountV2.get<DepositVaultsResponse>(
      `public/deposit_vaults`
    )
  return response as unknown as DepositVaultsResponse
}

export async function getAccountingAddressBalancesV2(): Promise<AccountingAddressBalanceV2Response> {
  const response =
    await authorizedRequestGasAccountV2.get<AccountingAddressBalanceV2Response>(
      `accounting-accounts/address-balances`
    )
  return response as unknown as AccountingAddressBalanceV2Response
}

export async function getAccountingHistoryV2(params?: {
  page?: number
  per?: number
  status?: string
}): Promise<AccountingHistoryV2Response> {
  const queryParams = new URLSearchParams()
  if (params?.page != null) queryParams.append("page", String(params.page))
  if (params?.per != null) queryParams.append("per", String(params.per))
  if (params?.status) queryParams.append("status", params.status)
  const query = queryParams.toString()
  const response =
    await authorizedRequestGasAccountV2.get<AccountingHistoryV2Response>(
      `accounting-accounts/history${query ? `?${query}` : ""}`
    )
  return response as unknown as AccountingHistoryV2Response
}

export async function createDepositV2(
  payload: CreateDepositV2Request
): Promise<DepositV2Response> {
  const response = await authorizedRequestGasAccountV2.post<DepositV2Response>(
    `deposits`,
    payload
  )
  return response as unknown as DepositV2Response
}

export async function updateDepositV2(
  depositId: string,
  payload: UpdateDepositV2Request
): Promise<DepositV2Response> {
  const response = await authorizedRequestGasAccountV2.patch<DepositV2Response>(
    `deposits/${depositId}`,
    payload
  )
  return response as unknown as DepositV2Response
}

export async function getDepositV2(
  depositId: string
): Promise<DepositV2Response> {
  const response = await authorizedRequestGasAccountV2.get<DepositV2Response>(
    `deposits/${depositId}`
  )
  return response as unknown as DepositV2Response
}

export async function quoteJpycDepositV2(
  payload: JpycDepositQuoteV2Request
): Promise<JpycDepositQuoteV2Response> {
  const response =
    await authorizedRequestGasAccountV2.post<JpycDepositQuoteV2Response>(
      `deposits/jpyc/quote`,
      payload
    )
  return response as unknown as JpycDepositQuoteV2Response
}

export async function createJpycDepositV2(
  payload: CreateJpycDepositV2Request
): Promise<DepositV2Response> {
  const response = await authorizedRequestGasAccountV2.post<DepositV2Response>(
    `deposits/jpyc`,
    payload
  )
  return response as unknown as DepositV2Response
}

// Backward-compatible aliases. Prefer explicit V1/V2 names in new code.
export const getGasAccountTransactions = getGasAccountTransactionsV1
export const getGasAccountTransactionById = getGasAccountTransactionByIdV1
export const getStableTokens = getStableTokensV1
export const getDepositVaults = getDepositVaultsV1
export const getGasAccountBalance = getGasAccountBalanceV1
export const updateGasAccountBalances = updateGasAccountBalancesV1
export const depositGasAccountTransaction = depositGasAccountTransactionV1
export const postUseGasAccountBalance = postUseGasAccountBalanceV1
